// Manual entry works with time points ("06:30 travel starts, 07:15 work starts, 16:45 end") —
// what people note down. Each point starts a block that runs until the next point; END closes
// the day or leaves an unpaid gap before the next point. A time earlier than the one before
// belongs to the next day (night shift). The server stores the resulting blocks (segments).
import dayjs from "dayjs";

export const STAMP_TYPES = ["WORK", "TRAVEL", "BREAK", "END"];

let seq = 0;
export const newStamp = (type = "WORK", time = "") => ({
  key: `s${Date.now()}${(seq += 1)}`,
  time,
  type,
  project: null,
  paid: true,
  note: ""
});

// What usually comes next, so adding a point needs no extra click most of the time
export const guessNextType = previous =>
  ({ TRAVEL: "WORK", WORK: "BREAK", BREAK: "WORK", END: "WORK" }[previous?.type] || "WORK");

export const TEMPLATES = [
  {
    key: "travel",
    label: "Travel → work → travel home",
    types: ["TRAVEL", "WORK", "TRAVEL", "END"]
  },
  { key: "break", label: "Work with break", types: ["WORK", "BREAK", "WORK", "END"] },
  { key: "work", label: "Work only", types: ["WORK", "END"] },
  { key: "split", label: "Split shift", types: ["WORK", "END", "WORK", "END"] },
  {
    key: "sites",
    label: "Two sites",
    types: ["TRAVEL", "WORK", "TRAVEL", "WORK", "TRAVEL", "END"]
  }
];

const parseTime = value => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value || "");
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  return h < 24 && m < 60 ? h * 60 + m : null;
};

/**
 * Time points of `date` (YYYY-MM-DD, local time) → blocks for the API.
 * Returns { segments, stampOf (segment index → stamp index), errors: [{ code, index }], offsets }
 * `running`: the last point may stay open (today, still working).
 */
export function stampsToSegments(stamps, date, { running = false } = {}) {
  const errors = [];
  const base = dayjs(date).startOf("day");
  const absolute = [];
  const offsets = [];
  let dayOffset = 0;
  let previous = null;
  stamps.forEach((stamp, index) => {
    const minutes = parseTime(stamp.time);
    if (minutes === null) {
      errors.push({ code: "TIME_MISSING", index });
      absolute.push(null);
      offsets.push(dayOffset);
      return;
    }
    if (previous !== null && minutes === previous) errors.push({ code: "SAME_TIME", index });
    if (previous !== null && minutes < previous) dayOffset += 1;
    previous = minutes;
    offsets.push(dayOffset);
    absolute.push(
      base
        .add(dayOffset, "day")
        .hour(Math.floor(minutes / 60))
        .minute(minutes % 60)
        .toDate()
    );
  });

  const segments = [];
  const stampOf = [];
  stamps.forEach((stamp, index) => {
    if (stamp.type === "END") return;
    const next = stamps[index + 1];
    if (!next && !running) errors.push({ code: "END_MISSING", index });
    if (!absolute[index]) return;
    segments.push({
      _id: stamp.segmentId,
      source: stamp.source,
      type: stamp.type,
      start: absolute[index],
      end: next ? absolute[index + 1] : null,
      project: stamp.type === "BREAK" ? null : stamp.project || null,
      paid: stamp.type === "TRAVEL" ? stamp.paid !== false : true,
      note: stamp.note || ""
    });
    stampOf.push(index);
  });
  return { segments, stampOf, errors, offsets };
}

/** Saved blocks → time points (a gap or the day's end becomes an END point). */
export function segmentsToStamps(segments) {
  const sorted = [...(segments || [])].sort((a, b) => new Date(a.start) - new Date(b.start));
  const stamps = [];
  let previousEnd = null;
  sorted.forEach(segment => {
    const start = dayjs(segment.start);
    if (previousEnd && !previousEnd.isSame(start)) {
      stamps.push(newStamp("END", previousEnd.format("HH:mm")));
    }
    stamps.push({
      ...newStamp(segment.type, start.format("HH:mm")),
      segmentId: segment._id,
      source: segment.source,
      project: segment.project?._id || segment.project || null,
      paid: segment.paid !== false,
      note: segment.note || ""
    });
    previousEnd = segment.end ? dayjs(segment.end) : null;
  });
  if (previousEnd) stamps.push(newStamp("END", previousEnd.format("HH:mm")));
  return stamps;
}

export const formatMinutes = minutes => {
  const total = Math.round(Math.abs(minutes || 0));
  return `${minutes < 0 ? "−" : ""}${Math.floor(total / 60)}:${String(total % 60).padStart(
    2,
    "0"
  )}`;
};
